
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { exportDescriptionsToCSV } from "@/lib/api/export";
import { useUserSettings } from "@/hooks/useUserSettings";

// Define a more precise type for the raw data returned from Supabase
interface RawDescription {
  original_text: string;
  generated_description: string;
  improved_description?: string | null;
  article_number?: string | null;
  product_details?: string | null;
  collections?: {
    organization_id?: string | null;
  } | null;
}

// Our expected Description type after validation
interface Description {
  original_text: string;
  generated_description: string;
  improved_description?: string;
  article_number?: string;
  product_details?: string;
  collections?: {
    organization_id?: string;
  };
}

export const useCollectionExport = (collectionId: string) => {
  const { toast } = useToast();
  const { settings } = useUserSettings();
  const showProductDetails = settings?.show_product_details === true;

  const { data: descriptions } = useQuery({
    queryKey: ["approvedDescriptions", collectionId, showProductDetails],
    queryFn: async () => {
      console.log('Fetching approved descriptions for collection:', collectionId);
      
      if (!collectionId) return [];
      
      try {
        // Always include product_details in the query, regardless of settings
        const columns = 'original_text, generated_description, improved_description, article_number, product_details';
        
        const { data, error } = await supabase
          .from("product_descriptions")
          .select(`${columns}, collections!inner (organization_id)`)
          .eq("collection_id", collectionId)
          .eq("status", "approved");

        if (error) {
          console.error('Error fetching approved descriptions:', error);
          throw error;
        }

        if (!data || !Array.isArray(data)) {
          console.warn('No descriptions data returned or data is not an array');
          return [];
        }

        // Safely filter and transform the data
        const validDescriptions: Description[] = [];
        
        for (const rawItem of data) {
          // First check if the item exists and is a non-null object
          if (!rawItem || typeof rawItem !== 'object') {
            console.warn('Invalid item found, skipping:', rawItem);
            continue;
          }
          
          // We've verified rawItem is a non-null object, now make TypeScript happy with a type assertion
          const item = rawItem as Record<string, unknown>;
          
          // Now we can safely check properties
          if ('original_text' in item && 
              typeof item.original_text === 'string' &&
              'generated_description' in item && 
              typeof item.generated_description === 'string') {
              
            // Only add items that match our expected structure
            validDescriptions.push({
              original_text: item.original_text,
              generated_description: item.generated_description,
              improved_description: item.improved_description !== null && item.improved_description !== undefined 
                ? String(item.improved_description) 
                : undefined,
              article_number: item.article_number !== null && item.article_number !== undefined 
                ? String(item.article_number) 
                : undefined,
              product_details: item.product_details !== null && item.product_details !== undefined 
                ? String(item.product_details) 
                : undefined,
              collections: item.collections !== null && item.collections !== undefined
                ? item.collections as { organization_id?: string } 
                : undefined
            });
          } else {
            console.warn('Item missing required fields, skipping:', item);
          }
        }

        console.log('Fetched valid descriptions:', validDescriptions.length);
        return validDescriptions;
      } catch (err) {
        console.error('Error in fetch query:', err);
        return [];
      }
    },
    enabled: !!collectionId,
  });

  const handleExport = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    if (!descriptions || !Array.isArray(descriptions) || descriptions.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no descriptions available to export",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const formattedDescriptions = descriptions.map(desc => {
        if (!desc || typeof desc !== 'object') {
          console.error('Invalid description object:', desc);
          return {
            originalText: 'Error: Invalid data',
            generatedDescription: 'Error: Invalid data',
            articleNumber: '',
            productDetails: ''
          };
        }

        return {
          originalText: desc.original_text || '',
          generatedDescription: desc.improved_description || desc.generated_description || '',
          articleNumber: desc.article_number || '',
          productDetails: desc.product_details || ''
        };
      });
        
      exportDescriptionsToCSV(formattedDescriptions);
      toast({
        title: "Success",
        description: "Descriptions exported successfully",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Error",
        description: "Failed to export descriptions",
        variant: "destructive",
      });
    }
  };

  return {
    descriptions,
    handleExport
  };
};
